import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import CustomLink from '../../CustomLink';
import ExpertSection from '../../Customer/components/ExpertSection';
import PractitionerBadge from '../../Customer/components/PractitionerBadge';
import ReviewsMobile from '../../Customer/components/ReviewsMobile';
import BoomTestimonials from '../../Customer/components/BoomTestimonials';
import MakeChoice from '../../Home/MakeChoice';
import IngredientsSlider from '../../Home/Slider/IngredientsSlider';
import DiscountBanner from '../../Home/DiscountBanner';
import Header from '../../../Layout/Header';
import { LP_LANGUAGE_OPTIONS, formatNumber } from '../../../utils/language';
import useReviewStats from '../../../hooks/useReviewStats';
import { Review1, Review2, Review3, Review4, ReviewRating } from '../../../Constants';
import useMeta from '../../../hooks/useMeta';
import { logo_home } from '../../../Data/Images/Home';
import '../../../assets/css/landing-pages.css';
import HeroImage from '../../../assets/hero-lp-pills.webp';
import WechselHero from '../../../assets/wechsel-hero.webp';

// Forked from src/Components/pages/PillsOrSachet/index.jsx for the menopause/
// perimenopause Meta ad set ("wechsel"). Same skeleton, same shared components,
// same brand tokens — copy and a few sections replaced per the research brief.
// Compliance note: no symptom-specific or hormonal-intervention claims, no
// outcome percentages tied to this life stage, no unsourced botanicals
// (sage/saffron/boswellia), no fabricated testimonial or pricing math. See
// the "Shift" section below — it is the section that keeps the rest of the
// page compliant by framing intent up front.
const K = {
  PageTitle:           'LP_Wechsel_PageTitle',
  HeroH1Part1:         'LP_Wechsel_HeroH1Part1',
  HeroH1Part2:         'LP_Wechsel_HeroH1Part2',
  HeroSubline:         'LP_Wechsel_HeroSubline',
  HeroCta:             'LP_Wechsel_HeroCta',
  StickyLine1:         'LP_Wechsel_StickyLine1',
  StickyLine2:         'LP_Wechsel_StickyLine2',
  HeroImageAlt:        'LP_Wechsel_HeroImageAlt',
  PouchImageAlt:       'LP_Wechsel_PouchImageAlt',
  HeroSocialProof:     'LP_Pills_HeroSocialProof',
  Stat1Val:            'LP_Pills_Stat1Val',
  Stat1Label:          'LP_Pills_Stat1Label',
  Stat2Val:            'LP_Pills_Stat2Val',
  Stat2Label:          'LP_Pills_Stat2Label',
  Stat3Val:            'LP_Pills_Stat3Val',
  Stat3Label:          'LP_Pills_Stat3Label',
  Stat4Val:            'LP_Pills_Stat4Val',
  Stat4Label:          'LP_Pills_Stat4Label',
  VignetteSectionLabel:'LP_Wechsel_VignetteSectionLabel',
  VignetteH2Line1:      'LP_Wechsel_VignetteH2Line1',
  VignetteH2Line2:      'LP_Wechsel_VignetteH2Line2',
  VignetteSubline:      'LP_Wechsel_VignetteSubline',
  Vignette1:            'LP_Wechsel_Vignette1',
  Vignette1Tag:         'LP_Wechsel_Vignette1Tag',
  Vignette1Detail:      'LP_Wechsel_Vignette1Detail',
  Vignette2:            'LP_Wechsel_Vignette2',
  Vignette2Tag:         'LP_Wechsel_Vignette2Tag',
  Vignette2Detail:      'LP_Wechsel_Vignette2Detail',
  Vignette3:            'LP_Wechsel_Vignette3',
  Vignette3Tag:         'LP_Wechsel_Vignette3Tag',
  Vignette3Detail:      'LP_Wechsel_Vignette3Detail',
  VignetteExpandCta:    'LP_Wechsel_VignetteExpandCta',
  ShiftSectionLabel:    'LP_Wechsel_ShiftSectionLabel',
  ShiftH2Line1:         'LP_Wechsel_ShiftH2Line1',
  ShiftH2Line2:         'LP_Wechsel_ShiftH2Line2',
  ShiftP1:              'LP_Wechsel_ShiftP1',
  ShiftP2:              'LP_Wechsel_ShiftP2',
  FocusSectionLabel:    'LP_Wechsel_FocusSectionLabel',
  FocusH2Line1:         'LP_Wechsel_FocusH2Line1',
  FocusH2Line2:         'LP_Wechsel_FocusH2Line2',
  FocusClaimLabel:      'LP_Wechsel_FocusClaimLabel',
  FocusMore:            'LP_Wechsel_FocusMore',
  FocusTab2:            'LP_Wechsel_FocusTab2',
  Focus2Title:          'LP_Wechsel_Focus2Title',
  Focus2Phys:           'LP_Wechsel_Focus2Phys',
  Focus2Desc:           'LP_Wechsel_Focus2Desc',
  FocusTab3:            'LP_Wechsel_FocusTab3',
  Focus3Title:          'LP_Wechsel_Focus3Title',
  Focus3Phys:           'LP_Wechsel_Focus3Phys',
  Focus3Desc:           'LP_Wechsel_Focus3Desc',
  FocusTab4:            'LP_Wechsel_FocusTab4',
  Focus4Title:          'LP_Wechsel_Focus4Title',
  Focus4Phys:           'LP_Wechsel_Focus4Phys',
  Focus4Desc:           'LP_Wechsel_Focus4Desc',
  FocusTab5:            'LP_Wechsel_FocusTab5',
  Focus5Title:          'LP_Wechsel_Focus5Title',
  Focus5Phys:           'LP_Wechsel_Focus5Phys',
  Focus5Desc:           'LP_Wechsel_Focus5Desc',
  FocusTab6:            'LP_Wechsel_FocusTab6',
  Focus6Title:          'LP_Wechsel_Focus6Title',
  Focus6Phys:           'LP_Wechsel_Focus6Phys',
  Focus6Desc:           'LP_Wechsel_Focus6Desc',
  FocusTab7:            'LP_Wechsel_FocusTab7',
  Focus7Title:          'LP_Wechsel_Focus7Title',
  Focus7Phys:           'LP_Wechsel_Focus7Phys',
  Focus7Desc:           'LP_Wechsel_Focus7Desc',
  IngrSubline:          'LP_Wechsel_IngrSubline',
  HowWorksHeading:      'LP_Wechsel_HowWorksHeading',
  Step1Title:           'LP_Wechsel_Step1Title',
  Step1Desc:            'LP_Wechsel_Step1Desc',
  Step2Title:           'LP_Wechsel_Step2Title',
  Step2Desc:            'LP_Wechsel_Step2Desc',
  Step3Title:           'LP_Wechsel_Step3Title',
  Step3Desc:            'LP_Wechsel_Step3Desc',
  ExpertHeading:        'LP_Wechsel_ExpertHeading',
  ReviewsHeading:       'LP_Wechsel_ReviewsHeading',
  FinalHeading:         'LP_Wechsel_FinalHeading',
  FinalSubline:         'LP_Wechsel_FinalSubline',
  FinalCta:             'LP_Wechsel_FinalCta',
  AdDisclosure:         'LP_Pills_AdDisclosure',
  PrivacyPolicy:        'LP_Pills_PrivacyPolicy',
  BadgeLabelLine1:      'LP_BadgeLabelLine1',
  BadgeLabelLine2:      'LP_BadgeLabelLine2',
  BadgeText:            'LP_BadgeText',
  CtaSub:               'LP_CtaSub',
};

const SERIF = { fontFamily: "'DM Serif Display', serif" };
const SANS  = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const CtaButton = ({ children, full = true }) => {
  const { metaEvent } = useMeta();
  return (
  <CustomLink
    to="/quiz"
    onClick={() => metaEvent('Lead')}
    className="lp-btn-cta"
    style={{ ...SANS, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, background: '#F6E147', color: '#103642',
      fontWeight: 700, fontSize: 19, borderRadius: 999,
      padding: '18px 36px', textDecoration: 'none',
      width: full ? '100%' : 'auto' }}
  >
    {children}
    <span aria-hidden="true">→</span>
  </CustomLink>
  );
};

const Label = ({ children, pill = false }) => {
  const base = {
    ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    textTransform: 'uppercase', color: '#116D6E',
    display: 'block', textAlign: 'center', margin: 0,
  };
  if (pill) return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ ...base, background: 'rgba(17,109,110,0.1)', borderRadius: 999, padding: '6px 16px' }}>
        {children}
      </span>
    </div>
  );
  return <p style={base}>{children}</p>;
};

const StepNumber = ({ n }) => (
  <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
    background: '#103642', color: '#F6E147', ...SANS,
    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {n}
  </div>
);

// Same visual treatment as PractitionerBadge, but with copy scoped to the
// hero on this page only — does not touch the shared component or its
// other usages on this or other pages.
const HeroExpertBadge = ({ t }) => (
  <div style={{
    border: '1px solid rgba(16,54,66,0.15)', borderRadius: 10,
    padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true">
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
      <div style={{ textAlign: 'center' }}>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine1')}
        </p>
        <p style={{ ...SANS, fontSize: 8, fontWeight: 800, letterSpacing: '0.1em',
          textTransform: 'uppercase', color: '#103642', margin: 0, lineHeight: 1.3 }}>
          {t('LP_BadgeLabelLine2')}
        </p>
      </div>
      <svg width="11" height="24" viewBox="0 0 16 34" fill="none" aria-hidden="true" style={{ transform: 'scaleX(-1)' }}>
        <path d="M9 33 C8.5 26 8 18 9 3" stroke="#103642" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.35"/>
        <path d="M9 29 C5 27 1 22 3 17 C6 19 9 24 9 29Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 22 C5 20 1 15 3 10 C6 12 9 17 9 22Z" fill="#103642" fillOpacity="0.6"/>
        <path d="M9 15 C5 13 2 8 4 4 C7 6 9 11 9 15Z" fill="#103642" fillOpacity="0.55"/>
        <path d="M9 8 C7 7 5 4 7 2 C8 4 9 6 9 8Z" fill="#103642" fillOpacity="0.5"/>
      </svg>
    </div>
    <div style={{ width: '1px', height: 26, background: 'rgba(16,54,66,0.15)', flexShrink: 0 }} />
    <p style={{ ...SANS, fontSize: 11, fontWeight: 600, color: '#103642',
      margin: 0, lineHeight: 1.4 }}>
      {t('LP_Wechsel_HeroExpertBadgeText')}
    </p>
  </div>
);

const Section = ({ bg = '#fff', py = 64, children, style = {}, maxW = 480, px = 24 }) => (
  <section style={{ background: bg, padding: `${py}px ${px}px`, ...style }}>
    <div style={{ maxWidth: maxW, margin: '0 auto' }}>
      {children}
    </div>
  </section>
);

const Stack = ({ gap = 24, align = 'stretch', children, style = {} }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap, alignItems: align, ...style }}>
    {children}
  </div>
);

const Chevron = ({ open, color = '#103642' }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
    style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
    <path d="M2.5 5l4.5 4.5L11.5 5" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// "Does this sound familiar" card — click to reveal a short, general (non-diagnostic,
// non-product) physiological explainer and a tag linking to the matching Focus Areas tab below.
const VignetteCard = ({ t, line, tag, detail, expandCta, isDesktop }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-card" style={{ borderRadius: 16, background: '#fff',
      border: '1px solid rgba(16,54,66,0.08)', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ ...SANS, width: '100%', textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: isDesktop ? 28 : 24, display: 'flex',
          flexDirection: 'column', gap: 12 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <p style={{ ...SANS, fontSize: 16, color: '#103642', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
            {line}
          </p>
          <Chevron open={open} color="rgba(16,54,66,0.4)" />
        </div>
        <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: '#116D6E', background: 'rgba(17,109,110,0.08)',
          borderRadius: 999, padding: '4px 10px', alignSelf: 'flex-start' }}>
          {tag}
        </span>
        {!open && (
          <span style={{ ...SANS, fontSize: 13, color: '#116D6E', fontWeight: 600 }}>
            {expandCta}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: `0 ${isDesktop ? 28 : 24}px ${isDesktop ? 24 : 20}px` }}>
          <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.6)', margin: 0, lineHeight: 1.65 }}>
            {detail}
          </p>
        </div>
      )}
    </div>
  );
};

// "Where we focus" — IM8-style pill-tab selector, one deep-dive card below.
// Physiology copy and the EFSA-authorized nutrient claim are kept visibly separate
// (own labeled line) rather than blended into one sentence, per the compliance brief.
const FocusTabs = ({ t, K, isDesktop }) => {
  // Mirrors the actual quiz/formula dosing logic (symptom → ingredient → dosing rule),
  // not an invented list. Sourced, EFSA-claimable ingredients get a "claim" card;
  // ingredients not yet in the catalogue (kind: 'status') get an honest formula-status
  // note instead of a fabricated claim — see the compliance note at the top of this file.
  // Hot flashes/night sweats is left out here: its only mapped ingredient (sage) isn't
  // sourced yet, so there's nothing real to show — see Focus1* keys, still unused on purpose.
  const [active, setActive] = useState(0);
  const tabs = [
    { tab: K.FocusTab2, title: K.Focus2Title, phys: K.Focus2Phys, claim: K.Focus2Desc },
    { tab: K.FocusTab3, title: K.Focus3Title, phys: K.Focus3Phys, claim: K.Focus3Desc },
    { tab: K.FocusTab4, title: K.Focus4Title, phys: K.Focus4Phys, claim: K.Focus4Desc },
    { tab: K.FocusTab5, title: K.Focus5Title, phys: K.Focus5Phys, claim: K.Focus5Desc },
    { tab: K.FocusTab6, title: K.Focus6Title, phys: K.Focus6Phys, claim: K.Focus6Desc },
    { tab: K.FocusTab7, title: K.Focus7Title, phys: K.Focus7Phys, claim: K.Focus7Desc },
  ];
  const current = tabs[active];

  return (
    <Stack gap={isDesktop ? 32 : 24}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 10 }}>
        {tabs.map(({ tab }, i) => (
          <button
            key={tab}
            onClick={() => setActive(i)}
            style={{ ...SANS, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', borderRadius: 999, padding: '10px 20px',
              cursor: 'pointer', transition: 'all 0.15s ease',
              border: active === i ? '1px solid #F6E147' : '1px solid rgba(255,255,255,0.2)',
              background: active === i ? '#F6E147' : 'transparent',
              color: active === i ? '#103642' : 'rgba(255,255,255,0.75)' }}
          >
            {t(tab)}
          </button>
        ))}
        <span style={{ ...SANS, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', borderRadius: 999, padding: '10px 20px',
          border: '1px dashed rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.5)' }}>
          {t(K.FocusMore)}
        </span>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20, padding: isDesktop ? 40 : 24 }}>
        <Stack gap={20}>
          <h3 style={{ ...SERIF, fontSize: isDesktop ? 30 : 24, lineHeight: 1.2, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(current.title)}
          </h3>
          <p style={{ ...SANS, fontSize: 15, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.7 }}>
            {t(current.phys)}
          </p>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 20,
            display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
              <circle cx="10" cy="10" r="9" stroke="#F6E147" strokeWidth="1.4" />
              <path d="M6 10.2l2.6 2.6L14 7.5" stroke="#F6E147" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <p style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
                textTransform: 'uppercase', color: '#F6E147', margin: '0 0 6px 0' }}>
                {t(K.FocusClaimLabel)}
              </p>
              <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.85)', margin: 0, lineHeight: 1.6 }}>
                {t(current.claim)}
              </p>
            </div>
          </div>
        </Stack>
      </div>
    </Stack>
  );
};

const StickyBar = ({ t, K, isDesktop }) => {
  const { metaEvent } = useMeta();

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: '#103642', padding: isDesktop ? '14px 40px' : '14px 20px',
      boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
    }}>
      <div style={{
        maxWidth: isDesktop ? 1200 : '100%',
        margin: '0 auto',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <p style={{ ...SANS, fontSize: 14, color: 'rgba(255,255,255,0.8)',
          margin: 0, lineHeight: 1.3, flex: 1 }}>
          {t(K.StickyLine1)}<br />
          <span style={{ color: '#F6E147', fontWeight: 600 }}>{t(K.StickyLine2)}</span>
        </p>
        <CustomLink to="/quiz" onClick={() => metaEvent('Lead')} className="lp-sticky-cta" style={{
          ...SANS, background: '#F6E147', color: '#103642',
          fontWeight: 700, fontSize: 15, borderRadius: 999,
          padding: '12px 22px', textDecoration: 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {t(K.HeroCta)} →
        </CustomLink>
      </div>
    </div>
  );
};

const Wechsel = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const reviewStats = useReviewStats();
  const fmtRating = (value) => formatNumber(value, i18n.language, { maximumFractionDigits: 2 });
  const fmtCount = (value) => formatNumber(value, i18n.language);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= 1200
  );

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // German is the default for this page regardless of the sitewide i18n
  // fallback (English) unless the URL explicitly says ?lang=en.
  useEffect(() => {
    const urlLang = (searchParams.get('lang') || '').toLowerCase();
    const lng = urlLang === 'en' ? 'en' : 'de';
    if (i18n.language !== lng) i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxW = isDesktop ? 1200 : 480;
  const px = isDesktop ? 80 : 24;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', ...SANS, paddingBottom: 72 }}>
      <Helmet>
        <title>{t(K.PageTitle)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <DiscountBanner />
      <Header languageOptions={LP_LANGUAGE_OPTIONS} />
      <StickyBar t={t} K={K} isDesktop={isDesktop} />

      {/* ── Hero ── */}
      {isDesktop ? (
        <section style={{ background: '#F9F9F9', overflow: 'hidden' }}>
          <div style={{
            maxWidth: 1400, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '52% 48%',
            minHeight: 600,
          }}>
            <div style={{ padding: '40px 64px 40px 80px', display: 'flex', alignItems: 'flex-start' }}>
              <Stack gap={20} style={{ width: '100%', maxWidth: 560 }}>
                <HeroExpertBadge t={t} />
                <h1 style={{ ...SERIF, fontSize: 58, lineHeight: 1.06, fontWeight: 400,
                  color: '#103642', margin: 0 }}>
                  {t(K.HeroH1Part1)}<br />
                  <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
                </h1>
                <p style={{ ...SANS, fontSize: 18, color: 'rgba(16,54,66,0.7)',
                  lineHeight: 1.7, margin: 0 }}>
                  {t(K.HeroSubline)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#F6E147', fontSize: 17, letterSpacing: '-1px' }}>★★★★★</span>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                    {t(K.HeroSocialProof, {
                      rating: fmtRating(reviewStats?.average || 4.88),
                      count: fmtCount(reviewStats?.count || 5000),
                    })}
                  </p>
                </div>
                <Stack gap={12} style={{ maxWidth: 380 }}>
                  <CtaButton full={false}>{t(K.HeroCta)}</CtaButton>
                  <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                    margin: 0, textAlign: 'center' }}>
                    {t(K.CtaSub)}
                  </p>
                </Stack>
              </Stack>
            </div>
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <img src={WechselHero} alt={t(K.HeroImageAlt)}
                style={{ width: '100%', height: '100%', objectFit: 'cover',
                  objectPosition: 'center top', display: 'block' }} loading="eager" />
            </div>
          </div>
        </section>
      ) : (
        <section style={{ background: '#F9F9F9' }}>
          <div style={{ maxWidth: 480, margin: '0 auto', padding: '28px 24px 40px' }}>
            <Stack gap={20}>
              <HeroExpertBadge t={t} />
              <h1 style={{ ...SERIF, fontSize: 40, lineHeight: 1.08, fontWeight: 400,
                color: '#103642', margin: 0 }}>
                {t(K.HeroH1Part1)}{' '}
                <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.HeroH1Part2)}</em>
              </h1>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.7)',
                lineHeight: 1.7, margin: 0 }}>
                {t(K.HeroSubline)}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#F6E147', fontSize: 15, letterSpacing: '-1px' }}>★★★★★</span>
                <p style={{ ...SANS, fontSize: 14, color: 'rgba(16,54,66,0.6)', margin: 0, whiteSpace: 'nowrap' }}>
                  {t(K.HeroSocialProof, {
                    rating: fmtRating(reviewStats?.average || 4.88),
                    count: fmtCount(reviewStats?.count || 5000),
                  })}
                </p>
              </div>
              <Stack gap={12}>
                <CtaButton>{t(K.HeroCta)}</CtaButton>
                <p style={{ ...SANS, fontSize: 13, color: 'rgba(16,54,66,0.4)',
                  textAlign: 'center', margin: 0 }}>
                  {t(K.CtaSub)}
                </p>
              </Stack>
            </Stack>
          </div>
          <img src={WechselHero} alt={t(K.HeroImageAlt)}
            style={{ width: '100%', height: 'auto', display: 'block' }} loading="eager" />
        </section>
      )}

      {/* ── Review ticker ── */}
      <div style={{ background: '#103642', padding: '8px 0', overflow: 'hidden' }}>
        <style>{`@keyframes riiseMarqueeWechsel { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
        <div
          style={{ display: 'flex', whiteSpace: 'nowrap', width: 'max-content',
            animation: 'riiseMarqueeWechsel 45s linear infinite' }}
        >
          {[0, 1].map((dup) => (
            <div key={dup} style={{ display: 'flex', alignItems: 'center',
              color: '#F9F9F9', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14 }}>
              {[
                t(Review1),
                t(Review2),
                t(Review3),
                t(Review4),
                <span key="rating" style={{ color: '#F6E147', fontWeight: 600 }}>
                  {t(ReviewRating, { rating: fmtRating(reviewStats?.average || 4.88) })}
                </span>,
              ].map((item, i) => (
                <React.Fragment key={i}>
                  <span style={{ padding: '0 16px' }}>{item}</span>
                  <span style={{ color: '#F6E147' }}>·</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── "You noticed some changes" — the problem section. Zero product mention, click to go deeper. ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={20}>
          <Label>{t(K.VignetteSectionLabel)}</Label>

          <h2 style={{ ...SERIF, fontSize: isDesktop ? 40 : 30, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.VignetteH2Line1)}<br />
            <em style={{ fontStyle: 'italic', color: '#116D6E' }}>{t(K.VignetteH2Line2)}</em>
          </h2>

          <p style={{ ...SANS, fontSize: 16, color: 'rgba(16,54,66,0.6)',
            lineHeight: 1.7, margin: 0, textAlign: 'center',
            maxWidth: isDesktop ? 640 : undefined, alignSelf: isDesktop ? 'center' : undefined }}>
            {t(K.VignetteSubline)}
          </p>

          <div style={{
            marginTop: 8,
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? '1fr 1fr 1fr' : undefined,
            flexDirection: isDesktop ? undefined : 'column',
            gap: 16,
          }}>
            {[
              { line: t(K.Vignette1), tag: t(K.Vignette1Tag), detail: t(K.Vignette1Detail) },
              { line: t(K.Vignette2), tag: t(K.Vignette2Tag), detail: t(K.Vignette2Detail) },
              { line: t(K.Vignette3), tag: t(K.Vignette3Tag), detail: t(K.Vignette3Detail) },
            ].map((v) => (
              <VignetteCard key={v.tag} t={t} isDesktop={isDesktop}
                line={v.line} tag={v.tag} detail={v.detail} expandCta={t(K.VignetteExpandCta)} />
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── "The Shift" — reframe (not a disease, riise addresses the nutritional shift, not the life stage
           itself) plus the trust/proof numbers, on dark background per design direction ── */}
      <Section bg="#103642" maxW={maxW} px={px} py={72}>
        <Stack gap={isDesktop ? 48 : 40}>
          <Stack gap={20}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#F6E147', background: 'rgba(246,225,71,0.12)',
                borderRadius: 999, padding: '6px 16px' }}>
                {t(K.ShiftSectionLabel)}
              </span>
            </div>

            <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0, textAlign: 'center' }}>
              {t(K.ShiftH2Line1)}<br />
              <em style={{ fontStyle: 'italic', color: '#F6E147' }}>{t(K.ShiftH2Line2)}</em>
            </h2>

            <Stack gap={16} style={{ maxWidth: isDesktop ? 640 : undefined, alignSelf: isDesktop ? 'center' : undefined }}>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.7, margin: 0, textAlign: 'center' }}>
                {t(K.ShiftP1)}
              </p>
              <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.75)',
                lineHeight: 1.7, margin: 0, textAlign: 'center' }}>
                {t(K.ShiftP2)}
              </p>
            </Stack>
          </Stack>

          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: isDesktop ? 40 : 32,
            display: 'grid',
            gridTemplateColumns: isDesktop ? '1fr 1fr 1fr 1fr' : '1fr 1fr',
            gap: isDesktop ? '0 40px' : '32px 0',
          }}>
            {[
              { val: K.Stat1Val, label: K.Stat1Label },
              { val: K.Stat2Val, label: K.Stat2Label },
              { val: K.Stat3Val, label: K.Stat3Label },
              {
                val: K.Stat4Val, label: K.Stat4Label,
                params: {
                  rating: fmtRating(reviewStats?.average || 4.9),
                  count: fmtCount(reviewStats?.count || 146),
                },
              },
            ].map(({ val, label, params }) => (
              <Stack key={val} gap={8} align="center" className="lp-stat" style={{ textAlign: 'center' }}>
                <p style={{ ...SERIF, fontSize: isDesktop ? 52 : 44, lineHeight: 1, fontWeight: 400,
                  color: '#F6E147', margin: 0 }}>
                  {t(val, params)}
                </p>
                <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.4, maxWidth: 140, margin: 0 }}>
                  {t(label, params)}
                </p>
              </Stack>
            ))}
          </div>
        </Stack>
      </Section>

      {/* ── Focus areas — IM8-style pill tabs + deep-dive card, dark background continues from "The Shift" ── */}
      <Section bg="#103642" maxW={maxW} px={px} py={72}>
        <Stack gap={isDesktop ? 40 : 32}>
          <Stack gap={20}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span style={{ ...SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: '#F6E147', background: 'rgba(246,225,71,0.12)',
                borderRadius: 999, padding: '6px 16px' }}>
                {t(K.FocusSectionLabel)}
              </span>
            </div>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0, textAlign: 'center' }}>
              {t(K.FocusH2Line1)}<br />
              <em style={{ fontStyle: 'italic', color: '#F6E147' }}>{t(K.FocusH2Line2)}</em>
            </h2>
          </Stack>

          <FocusTabs t={t} K={K} isDesktop={isDesktop} />
        </Stack>
      </Section>

      {/* ── Solution reveal — the hero photo (its overlaid copy already says "one pouch,
           everything included"), so no separate heading is needed here ── */}
      <section style={{ background: '#fff', paddingTop: 64, paddingBottom: 32 }}>
        <div style={{ maxWidth: maxW, margin: '0 auto', padding: `0 ${px}px` }}>
          <Stack gap={16} align="center" style={{ marginBottom: 48 }}>
            <img src={HeroImage} alt={t(K.PouchImageAlt)}
              style={{ width: '100%', maxWidth: isDesktop ? 520 : '100%', height: 'auto',
                borderRadius: 20, display: 'block' }} />
            <p style={{ ...SANS, fontSize: isDesktop ? 14 : 13, color: 'rgba(16,54,66,0.5)',
              margin: 0, textAlign: 'center' }}>
              {t(K.IngrSubline)}
            </p>
          </Stack>
        </div>
        <IngredientsSlider />
      </section>

      {/* ── How it works ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={isDesktop ? 48 : 40}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.HowWorksHeading)}
          </h2>

          {isDesktop ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 32 }}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} className="lp-card" style={{
                  background: '#fff', borderRadius: 16, padding: 28,
                  display: 'flex', flexDirection: 'column', gap: 16,
                }}>
                  <StepNumber n={n} />
                  <p style={{ ...SANS, fontSize: 17, fontWeight: 600,
                    color: '#103642', margin: 0 }}>
                    {t(title)}
                  </p>
                  <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                    margin: 0, lineHeight: 1.65 }}>
                    {t(desc)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <Stack gap={32}>
              {[
                { n: 1, title: K.Step1Title, desc: K.Step1Desc },
                { n: 2, title: K.Step2Title, desc: K.Step2Desc },
                { n: 3, title: K.Step3Title, desc: K.Step3Desc },
              ].map(({ n, title, desc }) => (
                <div key={n} style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <StepNumber n={n} />
                  <Stack gap={8}>
                    <p style={{ ...SANS, fontSize: 16, fontWeight: 600,
                      color: '#103642', margin: 0 }}>
                      {t(title)}
                    </p>
                    <p style={{ ...SANS, fontSize: 15, color: 'rgba(16,54,66,0.6)',
                      margin: 0, lineHeight: 1.65 }}>
                      {t(desc)}
                    </p>
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      </Section>

      {/* ── Mid-page CTA ── */}
      <Section bg="#103642" maxW={maxW} px={px}>
        <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#fff', margin: 0 }}>
            {t(K.FinalHeading)}
          </h2>
          <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.65)',
            margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
            {t(K.FinalSubline)}
          </p>
          <div style={{ width: isDesktop ? 'auto' : '100%' }}>
            <CtaButton full={!isDesktop}>{t(K.HeroCta)}</CtaButton>
          </div>
        </Stack>
      </Section>

      {/* ── Expert ── */}
      <Section bg="#fff" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ExpertHeading)}
          </h2>
          <ExpertSection />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: '100%', maxWidth: 450 }}>
              <PractitionerBadge />
            </div>
          </div>
        </Stack>
      </Section>

      {/* ── Video testimonials (real, existing content) ── */}
      <BoomTestimonials />

      {/* ── Comparison table ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <MakeChoice />
      </Section>

      {/* ── Final CTA ── */}
      <section style={{ background: '#103642', padding: `80px ${px}px` }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={24} align="center" style={{ textAlign: 'center' }}>
            <h2 style={{ ...SERIF, fontSize: isDesktop ? 40 : 28, lineHeight: 1.15, fontWeight: 400,
              color: '#fff', margin: 0 }}>
              {t(K.FinalHeading)}
            </h2>
            <p style={{ ...SANS, fontSize: 16, color: 'rgba(255,255,255,0.7)',
              margin: 0, lineHeight: 1.7, maxWidth: isDesktop ? 560 : undefined }}>
              {t(K.FinalSubline)}
            </p>
            <Stack gap={12} style={{ width: isDesktop ? 'auto' : '100%' }}>
              <CtaButton full={!isDesktop}>{t(K.FinalCta)}</CtaButton>
              <p style={{ ...SANS, fontSize: 13, color: 'rgba(255,255,255,0.4)',
                textAlign: 'center', margin: 0 }}>
                {t(K.CtaSub)}
              </p>
            </Stack>
          </Stack>
        </div>
      </section>

      {/* ── Reviews carousel ── */}
      <Section bg="#f5f7f7" maxW={maxW} px={px}>
        <Stack gap={32}>
          <h2 style={{ ...SERIF, fontSize: isDesktop ? 36 : 28, lineHeight: 1.15, fontWeight: 400,
            color: '#103642', margin: 0, textAlign: 'center' }}>
            {t(K.ReviewsHeading)}
          </h2>
          <ReviewsMobile />
        </Stack>
      </Section>

      {/* ── Footer ── */}
      <footer style={{ background: '#fff', borderTop: '1px solid #f0f0f0',
        padding: isDesktop ? '40px 80px' : '40px 24px' }}>
        <div style={{ maxWidth: maxW, margin: '0 auto' }}>
          <Stack gap={16} align="center">
            <img src={logo_home} alt="riise"
              style={{ height: 20, width: 'auto', opacity: 0.4 }} />
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
              <CustomLink to="/impressum"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                Impressum
              </CustomLink>
              <CustomLink to="/policy"
                style={{ ...SANS, fontSize: 12, color: 'rgba(16,54,66,0.4)', textDecoration: 'none' }}>
                {t(K.PrivacyPolicy)}
              </CustomLink>
            </div>
            <p style={{ ...SANS, fontSize: 11, color: 'rgba(16,54,66,0.3)',
              textAlign: 'center', margin: 0 }}>
              {t(K.AdDisclosure)} · {t('LP_SupplementDisclaimer')}
            </p>
          </Stack>
        </div>
      </footer>
    </div>
  );
};

export default Wechsel;
